import { inject, Injectable } from '@angular/core';
import { filter, map, Observable, takeUntil, tap } from 'rxjs';
import { ObjectTools } from '@sol/components';
import { AbstractAsmService } from '@asm/components-core/services/abstract-asm.service';
import { AsmAppContext, SessionService } from './session.service';

export enum AsmUiStateBadge {
  whatsNew = 'whats_new',
}

interface UiStateDto {
  ui_preferences: Record<string, unknown>;
  badges: Record<string, number>;
}

export class AsmUiState {
  private constructor(
    public readonly context: AsmAppContext | undefined,
    public readonly preferences: Record<string, unknown>,
    public readonly badges: Record<string, number>,
    public readonly fetched: boolean,
  ) {}

  public static of(): AsmUiState {
    return new AsmUiState(undefined, {}, {}, false);
  }

  public withContext(context: AsmAppContext | undefined): AsmUiState {
    return ObjectTools.copy<AsmUiState>(this, {
      context,
      preferences: {},
      badges: {},
      fetched: false,
    });
  }

  public withConfig(
    context: AsmAppContext,
    preferences: Record<string, unknown>,
    badges: Record<string, number>,
  ): AsmUiState {
    if (!ObjectTools.equals(this.context, context)) return this;
    return ObjectTools.copy<AsmUiState>(this, {
      preferences,
      badges,
      fetched: true,
    });
  }

  public withFailure(): AsmUiState {
    return ObjectTools.copy<AsmUiState>(this, { fetched: true });
  }

  public withWhatsNewChange(change: number | undefined): AsmUiState {
    if (!Object.hasOwn(this.badges, AsmUiStateBadge.whatsNew)) return this;
    const count = change === undefined ? 0 : Math.max(0, this.badges[AsmUiStateBadge.whatsNew] + change);
    return ObjectTools.copy<AsmUiState>(this, {
      badges: { ...this.badges, [AsmUiStateBadge.whatsNew]: count },
    });
  }
}

/** Preferences and navigation badges for the confirmed app context. */
@Injectable({ providedIn: 'root' })
export class AsmUiStateService extends AbstractAsmService<AsmUiState> {
  // Dependencies
  private readonly sessionService = inject(SessionService);

  constructor() {
    super('/api/v3/user/ui-state', AsmUiState.of());
    this.sessionService.whenContextReady().subscribe(context => {
      this.update(state => state.withContext(context));
      if (context !== undefined) {
        this.fetch(context).subscribe({ error: () => undefined });
      }
    });
  }

  public whenResolved(): Observable<AsmUiState> {
    return this.state$.pipe(filter(state => state.fetched));
  }

  public reportWhatsNewItemRead(context: AsmAppContext | undefined): void {
    this.reportWhatsNewChange(context, -1);
  }
  public reportWhatsNewItemUnread(context: AsmAppContext | undefined): void {
    this.reportWhatsNewChange(context, 1);
  }
  public reportAllWhatsNewRead(context: AsmAppContext | undefined): void {
    this.reportWhatsNewChange(context, undefined);
  }

  private reportWhatsNewChange(context: AsmAppContext | undefined, change: number | undefined): void {
    if (context === undefined || context !== this.state.context) return;
    this.update(state => state.withWhatsNewChange(change));
  }

  private fetch(context: AsmAppContext): Observable<void> {
    return this.httpGet<UiStateDto>('', { params: context.params }).pipe(
      takeUntil(this.sessionService.whenContextReady().pipe(filter(current => !ObjectTools.equals(current, context)))),
      tap({
        next: dto => this.update(state => state.withConfig(context, dto.ui_preferences, dto.badges)),
        error: () => this.update(state => state.withFailure()),
      }),
      map(() => undefined),
    );
  }
}
